import {
  PrimaryProductAccessType,
  ProductCategory,
  Outlet,
  Product,
} from '@collinsonx/utils/generatedTypes/graphql';
import { usePerVisitLimit } from 'config/features';
import { useFTWithOutlet } from 'config/features';
import {
  DEFAULT_CANCELLATION_PERIOD,
  DEFAULT_CONTEXT_PRODUCT_VALUE,
  DEFAULT_CURRENCY,
  DEFAULT_GUESTS_PARAMS,
  DEFAULT_MAX_BOOKING_PERIOD,
  DEFAULT_MAX_PARTY_SIZE,
  DEFAULT_MIN_BOOKING_PERIOD,
  DEFAULT_PRICE,
  DEFAULT_TIME_SLOT_PERIOD,
  FASTTRACK_IMAGE_URL,
  GUEST_TYPES,
} from 'config/productDefaultConfig';
import {
  MappedProductData,
  ProductValuationType,
} from 'types/mappedProductData';
import { resolvePdfGuideUrl } from 'utils/pdfGuideUrl';

export const convertOutletToProduct = (
  outlet: Outlet,
  product: Pick<
    Product,
    'id' | 'location' | 'partnerIntegrationData' | 'accessType'
  >,
  isLocationProduct = false,
  useReservationAccessType = false
): MappedProductData => {
  const { partnerIntegrationData } = product;
  const productAccessType =
    product.accessType ??
    (useReservationAccessType
      ? PrimaryProductAccessType.Reservation
      : PrimaryProductAccessType.ReservationFeeOnly);

  const productData = outlet.products.filter(
    (productItem) => productItem?.accessType === productAccessType
  )[0];

  const saleprice = productData?.salePrices[0]?.salePrice ?? DEFAULT_PRICE;

  const category = productData?.category ?? ProductCategory.Lounge;

  const guests: MappedProductData['guests'] = {};

  if (partnerIntegrationData?.maxGuests?.adult)
    guests[GUEST_TYPES.adults] = {
      ...DEFAULT_GUESTS_PARAMS[GUEST_TYPES.adults],
      isChargeable:
        partnerIntegrationData.maxGuests.adult.isChargeable ??
        DEFAULT_GUESTS_PARAMS.adults.isChargeable,
      isPartySizeImpacted:
        partnerIntegrationData.maxGuests.adult.isPartySizeImpacted,
      maxCountPerGuestType: partnerIntegrationData.maxGuests.adult.value,
      price: saleprice,
    };

  if (partnerIntegrationData?.maxGuests?.children)
    guests[GUEST_TYPES.children] = {
      ...DEFAULT_GUESTS_PARAMS[GUEST_TYPES.children],
      isChargeable:
        partnerIntegrationData.maxGuests.children.isChargeable ??
        DEFAULT_GUESTS_PARAMS.children.isChargeable,
      isPartySizeImpacted:
        partnerIntegrationData.maxGuests.children.isPartySizeImpacted,
      maxCountPerGuestType: partnerIntegrationData.maxGuests.children.value,
      price: saleprice,
    };

  if (partnerIntegrationData?.maxGuests?.infant)
    guests[GUEST_TYPES.infants] = {
      ...DEFAULT_GUESTS_PARAMS[GUEST_TYPES.infants],
      isChargeable:
        partnerIntegrationData.maxGuests.infant.isChargeable ??
        DEFAULT_GUESTS_PARAMS.infants.isChargeable,
      isPartySizeImpacted:
        partnerIntegrationData.maxGuests.infant.isPartySizeImpacted,
      maxCountPerGuestType: partnerIntegrationData.maxGuests.infant.value,
      price: saleprice,
    };

  // uncomment to enable seniors
  // if(partnerIntegrationData?.maxGuests?.senior) guests[GUEST_TYPES.seniors] = {
  //   ...DEFAULT_GUESTS_PARAMS[GUEST_TYPES.infants],
  //   isPartySizeImpacted: partnerIntegrationData.maxGuests.senior.isPartySizeImpacted,
  //   maxCountPerGuestType: partnerIntegrationData.maxGuests.senior.value,
  // }

  const isFastTrackProduct =
    useFTWithOutlet &&
    isLocationProduct &&
    category === ProductCategory.FastTrack;

  return {
    ...DEFAULT_CONTEXT_PRODUCT_VALUE,
    accessHours: outlet.accessTimes?.accessHours ?? null,
    accessPeriod: partnerIntegrationData?.accessPeriod ?? 3,
    accessType: productData?.accessType ?? productAccessType,
    airportCode: outlet.location.code ?? '',
    airportName: outlet.location.name ?? '',
    bookingFlowType: partnerIntegrationData?.bookingFlowType ?? '',
    bookingMaxPeriodAllowed:
      partnerIntegrationData?.bookingMaxPeriodAllowed ??
      DEFAULT_MAX_BOOKING_PERIOD,
    bookingMinPeriodAllowed:
      partnerIntegrationData?.bookingMinPeriodAllowed ??
      DEFAULT_MIN_BOOKING_PERIOD,
    cancellationPeriod:
      partnerIntegrationData?.cancellationPeriodAllowed ??
      DEFAULT_CANCELLATION_PERIOD,
    category,
    currency:
      outlet.products[0]?.salePrices[0]?.salePriceCurrency ?? DEFAULT_CURRENCY,
    guests,
    imageUrl: isFastTrackProduct
      ? FASTTRACK_IMAGE_URL
      : outlet.media?.mainImage?.asset?.url ?? '',
    legacyProduct: false,
    loungeCode: outlet.code ?? '',
    loungeID: outlet.id,
    loungeName: outlet.name,
    maxPartySize:
      partnerIntegrationData?.maxGuests?.partySize ?? DEFAULT_MAX_PARTY_SIZE,
    partnerID: partnerIntegrationData?.partnerIntegrationID ?? '',
    partnerProductID: partnerIntegrationData?.partnerProductID ?? '',
    ...(isFastTrackProduct
      ? { pdfGuideUrl: resolvePdfGuideUrl(partnerIntegrationData?.pdfGuideUrl) }
      : {}),
    productID: product.id,
    saleprice,
    stripeID: partnerIntegrationData?.stripeProductID || product.id,
    terminal: outlet.location.terminal ?? product?.location?.terminal ?? '',
    timeSlotPeriod:
      partnerIntegrationData?.timeSlotPeriod ?? DEFAULT_TIME_SLOT_PERIOD,
    timezone: outlet.location.timezone ?? product?.location?.timezone ?? '',
    viewOnly: saleprice === 0,
  };
};

export const convertLocationToProduct = (
  locationProduct: Product
): MappedProductData => {
  const { partnerIntegrationData } = locationProduct;

  return {
    ...DEFAULT_CONTEXT_PRODUCT_VALUE,
    accessPeriod: partnerIntegrationData?.accessPeriod ?? 3,
    accessType:
      locationProduct.accessType ?? PrimaryProductAccessType.ReservationFeeOnly,
    airportCode: locationProduct.location?.code ?? '',
    airportName: locationProduct.location?.name ?? '',
    bookingMaxPeriodAllowed:
      partnerIntegrationData?.bookingMaxPeriodAllowed ??
      DEFAULT_MAX_BOOKING_PERIOD,
    bookingMinPeriodAllowed:
      partnerIntegrationData?.bookingMinPeriodAllowed ??
      DEFAULT_MIN_BOOKING_PERIOD,
    cancellationPeriod:
      partnerIntegrationData?.cancellationPeriodAllowed ??
      DEFAULT_CANCELLATION_PERIOD,
    category: locationProduct.category ?? ProductCategory.Lounge,
    currency: locationProduct.salePrices?.[0]?.salePriceCurrency ?? '',
    imageUrl: FASTTRACK_IMAGE_URL ?? '',
    loungeCode: locationProduct?.code ?? '',
    loungeID: locationProduct?.id ?? '',
    loungeName: locationProduct.name ?? '',
    maxPartySize:
      partnerIntegrationData?.maxGuests?.partySize ?? DEFAULT_MAX_PARTY_SIZE,
    partnerID: partnerIntegrationData?.partnerIntegrationID ?? '',
    partnerProductID: partnerIntegrationData?.partnerProductID ?? '',
    pdfGuideUrl:
      resolvePdfGuideUrl(partnerIntegrationData?.pdfGuideUrl) ||
      DEFAULT_CONTEXT_PRODUCT_VALUE.pdfGuideUrl,
    productID: locationProduct?.id ?? '',
    saleprice: locationProduct.salePrices?.[0]?.salePrice ?? 0,
    stripeID: locationProduct.salePrices?.[0]?.stripePriceID ?? '',
    terminal: locationProduct.location?.terminal ?? '',
    timeSlotPeriod:
      partnerIntegrationData?.timeSlotPeriod ?? DEFAULT_TIME_SLOT_PERIOD,
    timezone: locationProduct?.location?.timezone ?? 'UTC',
  };
};

export const convertValuationToProduct = (valuation: ProductValuationType) => {
  const perVisitLimit = usePerVisitLimit
    ? valuation.dealEntitlements?.find(
        (deal) => typeof deal?.perVisitLimit === 'number'
      )?.perVisitLimit
    : undefined;

  return {
    currency: valuation.priceDetails.currency,
    entitlements: usePerVisitLimit
      ? valuation.entitlements?.map((entitlement) => ({
          ...entitlement,
          perVisitLimit: entitlement.perVisitLimit ?? perVisitLimit,
        }))
      : valuation.entitlements,
    // Use `??` so a legitimate `memberPrice` of `0` (genuinely free for
    // members) is honoured instead of falling back to `finalPrice`.
    saleprice:
      valuation.priceDetails.memberPrice ?? valuation.priceDetails.finalPrice,
  };
};
